import { create } from "zustand";

/**
 * Pilihan tema terang/gelap.
 *
 * Urutan penentuan tema saat aplikasi dibuka:
 *   1. pilihan yang pernah disimpan pengguna (localStorage), kalau ada;
 *   2. kalau belum pernah memilih, ikut setelan sistem
 *      (`prefers-color-scheme`).
 *
 * Nilai tema dipasang sebagai atribut `data-theme` di elemen <html>, tempat
 * token warna di src/styles/_theme.scss dibaca.
 *
 * Nilai awalnya juga sudah dipasang oleh skrip kecil di public/index.html
 * supaya halaman tidak sempat berkedip terang sebelum React mount. Keduanya
 * membaca kunci localStorage yang sama.
 */

export const THEME_STORAGE_KEY = "theme";

const LIGHT = "light";
const DARK = "dark";

const isValidTheme = (value) => value === LIGHT || value === DARK;

/** localStorage bisa melempar error (mode privat, site data diblokir). */
const readStoredTheme = () => {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isValidTheme(stored) ? stored : null;
  } catch (error) {
    return null;
  }
};

const persistTheme = (theme) => {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch (error) {
    // Pilihan tetap berlaku untuk sesi ini walau gagal disimpan.
  }
};

const prefersDarkSystem = () => {
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch (error) {
    return false;
  }
};

const applyTheme = (theme) => {
  document.documentElement.setAttribute("data-theme", theme);
};

const resolveInitialTheme = () =>
  readStoredTheme() || (prefersDarkSystem() ? DARK : LIGHT);

const useThemeStore = create((set, get) => {
  const initialTheme = resolveInitialTheme();
  // Menyelaraskan atribut dengan nilai yang dipakai store, kalau-kalau skrip
  // di index.html tidak sempat jalan.
  applyTheme(initialTheme);

  return {
    theme: initialTheme,
    isDark: initialTheme === DARK,

    setTheme: (theme) => {
      if (!isValidTheme(theme)) return;
      persistTheme(theme);
      applyTheme(theme);
      set({ theme, isDark: theme === DARK });
    },

    toggleTheme: () => {
      get().setTheme(get().theme === DARK ? LIGHT : DARK);
    },
  };
});

export default useThemeStore;
